import React, { useEffect, useState } from "react";

import { useForm } from "react-hook-form";

import { keyframes, useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import { apiFetch } from "../../../../apiBindings";
import pushToAnalytics from "../../../../utils/analytics";
import StorageHandler from "../../../../utils/StorageHandler";
import Backdrop from "../../Backdrop";
import { BlurOther } from "../../Blur";
import BaseInputs from "../../ContactForm/BaseInputs";
import FormFooter from "../../ContactForm/FormFooter";
import FormNotifications from "../../ContactForm/FormNotifications";
import ReactHookInput from "../../forms/ReactHookInput";
import Icon from "../../icons/Icon";
import NewHeadline from "../../NewHeadline";
import Phone from "../Phone";
import useStore from "./store";

const slideIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(-100%);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const slideOut = keyframes`
    from {
        opacity: 1;
        transform: translateY(0);
    }

    to {
        opacity: 0;
        transform: translateY(-100%);
    }
`;

const Container = styled.div`
    padding: 20px;
    background-color: white;
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 60px;
    z-index: 111;
    transform: translateY(-100%);
    opacity: 0;
    width: 100%;
    max-height: 90%;
    min-height: 300px;
    text-align: left;
    overflow-y: scroll;

    &.open {
        opacity: 0;
        transform: translateY(-100%);
        animation: ease 0.25s ${slideIn} forwards;
    }

    &.close {
        opacity: 1;
        transform: translateY(0);
        animation: ease 0.25s ${slideOut} forwards;
    }

    ${({ theme }) => theme.breakpoints.from.medium.css`
        width: 380px;
        right: 100px;
        padding: 30px;
        top: 100px;
        overflow-y: auto;
        border-radius: 0 0 5px 5px;
    `}

    &:hover {
        visibility: visible;
    }
`;

const ContactFormBackdrop = styled(Backdrop)`
    z-index: 101;
`;

const IconWrapper = styled.div`
    display: none;
    align-self: flex-end;
    cursor: pointer;
    ${({ theme }) => theme.breakpoints.from.medium.css`
        display: flex;
        margin-bottom: 25px;
    `}
`;

const CloseIcon = styled(({ onClick, ...props }) => {
    const theme = useTheme();
    return (
        <button type="button" onClick={onClick} {...props}>
            <Icon
                size="16px"
                iconName="close"
                color={theme.colors.blue.toString()}
            />
            <p>Schließen</p>
        </button>
    );
})`
    all: unset;
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    cursor: pointer;
    opacity: 1;
    transition: opacity 100ms;

    ${({ theme }) => theme.selectors.hover.css`
        opacity: 0.7;
    `}

    p {
        margin: 0;
        font-size: 0.8rem;
        color: ${({ theme }) => theme.colors.blue.toString()};
    }
`;

const PhoneContainer = styled.div`
    margin: 20px 0;
    width: fit-content;
    text-decoration: underline;
    font-size: 18px !important;
`;

interface ContactFormInputs {
    name: string;
    phone: string;
    email: string;
    subject: string;
    message: string;
    isPrivacyPolicyAccepted: boolean;
}

/**
 * If the user didn't sent something or the last sending ist older or equals 5 minutes ago,
 * he can send again.
 */
const canSend = (timestamp: string | null) => {
    if (timestamp === null) {
        return true;
    }
    return Math.floor((Date.now() - parseInt(timestamp)) / 1000 / 60) >= 5;
};

const ContactForm = () => {
    const { modalState, setModalState, setContactFormVisible } = useStore();
    const storage = new StorageHandler();

    useEffect(() => {
        setModalState("open");
    }, [setModalState]);

    const { handleSubmit, control } = useForm<ContactFormInputs>({
        defaultValues: {
            name: "",
            phone: "",
            email: "",
            subject: "",
            message: "",
            isPrivacyPolicyAccepted: false,
        },
        mode: "onSubmit",
    });

    const [sending, setSending] = useState(false);
    const [sendingError, setSendingError] = useState(false);
    const [emailSent, setEmailSent] = useState(
        !canSend(storage.get("general:contact-form:sent-at", null))
    );

    const getZipcode = () => {
        const lastCalculator = storage.get("last-calculator");
        if (lastCalculator === null) {
            return null;
        }
        const zipcodes = {
            holzpellets: storage.get("pel:zipcode"),
            heizoel: storage.get("hel:zipcode"),
            erdgas: storage.get("gas:zipcode"),
            strom: storage.get("ele:zipcode"),
        };
        return zipcodes[lastCalculator as keyof typeof zipcodes] || null;
    };

    const onSubmit = (inputs: ContactFormInputs) => {
        setSending(true);
        const payload = { ...inputs, zipcode: getZipcode() };
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        apiFetch("/contact/general", {
            method: "post",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
            body: JSON.stringify(payload),
        })
            .then((response) => response.json())
            .then((responseData) => {
                if (responseData.success) {
                    storage.set("general:contact-form:sent-at", Date.now());
                    storage.set("app:uid", responseData.appUid);
                    setEmailSent(true);
                    setSendingError(false);
                    pushToAnalytics("contact-form:sent", {
                        form: "general:contact-form",
                    });
                } else {
                    setSendingError(true);
                }
                setSending(false);
            });
    };

    return (
        <>
            <ContactFormBackdrop
                open={modalState === "open"}
                onClick={() => setContactFormVisible(false)}
            />
            {modalState === "open" && <BlurOther except={["Nav"]} />}

            <Container className={modalState}>
                {!sending && !sendingError && (
                    <IconWrapper>
                        <CloseIcon
                            onClick={() => setContactFormVisible(false)}
                        />
                    </IconWrapper>
                )}

                <FormNotifications
                    sendingError={sendingError}
                    sending={sending}
                    emailSent={emailSent}
                    onRetry={() => setSendingError(false)}
                >
                    <NewHeadline lookLike="2" color="blue">
                        Dein Kontakt zu uns
                    </NewHeadline>
                    <PhoneContainer>
                        <Phone
                            defaultPhoneNumber="+49 4262 797"
                            telName="Hoyer Zentrale"
                        />
                    </PhoneContainer>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <BaseInputs
                            control={control}
                            translations={{
                                name: "Name (Pflichtfeld)",
                                email: "E-Mail (Pflichtfeld)",
                                phone: "Telefon (Pflichtfeld)",
                                messageErrorEmail:
                                    "Gib bitte eine gültige E-Mail-Adresse ein.",
                                messageErrorPhone:
                                    "Gib bitte eine gültige Telefonnummer ein.",
                            }}
                        />
                        <ReactHookInput
                            name="subject"
                            fullWidth
                            placeholder="Betreff (Pflichtfeld)"
                            rules={{ required: true, maxLength: 100 }}
                            control={control}
                        />
                        <FormFooter
                            control={control}
                            gtag="main:contact-form:button"
                            submitLabel="Anfrage senden"
                        />
                    </form>
                </FormNotifications>
            </Container>
        </>
    );
};

export default ContactForm;
